import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateDemoFeedbackFormTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const optionScaleId = uuidv4();
  const optionScaleBlockId = uuidv4();
  const longText1Id = uuidv4();
  const longText1BlockId = uuidv4();
  const multipleChoice1Id = uuidv4();
  const multipleChoice1BlockId = uuidv4();
  const multipleChoice2Id = uuidv4();
  const multipleChoice2BlockId = uuidv4();
  const longText2Id = uuidv4();
  const longText2BlockId = uuidv4();
  const longText3Id = uuidv4();
  const longText3BlockId = uuidv4();
  const longText4Id = uuidv4();
  const longText4BlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const mc1Option1Id = uuidv4();
  const mc1Option2Id = uuidv4();
  const mc1Option3Id = uuidv4();
  const mc2Option1Id = uuidv4();
  const mc2Option2Id = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const multipleChoice2MediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  // Generate new UUIDs for logic items and conditions
  const logicItem1Id = uuidv4();
  const logicItem2Id = uuidv4();
  const logicItem3Id = uuidv4();
  const logicItem4Id = uuidv4();
  const condition1Id = uuidv4();
  const condition2Id = uuidv4();

  return {
    ...themes.sunsetGold,
    "collectionId": collectionId,
    "name": "Demo Feedback Form",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Tried out our product yet?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'd love to hear how your demo went and what stood out to you.",
        "thirdContent": "Give feedback",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "a woman wearing headphones and holding a cell phone",
          "link": "https://unsplash.com/photos/agINaOByj4k/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyfHxoYXBweSUyMHBob25lfGVufDB8fHx8MTc2NDQ2NjQzOXww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "Before we begin, what's your name?",
        "manditory": false,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Just your first name is perfect.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": emailId,
        "blockId": emailBlockId,
        "content": `Awesome, {{${contactInfoBlockId}__first_name}}! What's your email?`,
        "manditory": false,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll send over a 15% discount as a thank-you.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "OPTION_SCALE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": optionScaleId,
        "blockId": optionScaleBlockId,
        "content": "How would you rate your experience with the demo overall?",
        "manditory": false,
        "order": 3,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 1,
        "maxRange": 10,
        "minLabel": "Didn't like it",
        "maxLabel": "Loved it",
        "secondaryContent": "Share your first impression.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": longText1Id,
        "blockId": longText1BlockId,
        "content": `What led you to choose that rating of  {{${optionScaleBlockId}}}?`,
        "manditory": false,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "If you're up for it, tell us what influenced your score. (Feel free to skip this one.)",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice1Id,
        "blockId": multipleChoice1BlockId,
        "content": "What were you planning to use the product for?",
        "manditory": false,
        "order": 5,
        "selectOptions": [
          {
            "id": mc1Option1Id,
            "order": 0,
            "value": "For the company I work at"
          },
          {
            "id": mc1Option2Id,
            "order": 1,
            "value": "For my own business"
          },
          {
            "id": mc1Option3Id,
            "order": 2,
            "value": "For personal projects"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Choose the option that fits best.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice2Id,
        "blockId": multipleChoice2BlockId,
        "content": "After trying it out, would you consider subscribing?",
        "manditory": true,
        "order": 6,
        "selectOptions": [
          {
            "id": mc2Option1Id,
            "order": 0,
            "value": "Yes"
          },
          {
            "id": mc2Option2Id,
            "order": 1,
            "value": "No"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": multipleChoice2MediaId,
          "type": "image",
          "name": "a woman wearing headphones sitting on a bed",
          "link": "https://unsplash.com/photos/7njkFeApWKA/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyfHxwaG9uZSUyMGhhcHB5JTIwb3JhbmdlfGVufDB8fHx8MTc2NDQ2NjU0OXww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": longText3BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice2Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition1Id,
                "value": "false",
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice2BlockId,
                "parentLogicItemId": logicItem1Id
              }
            ]
          },
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem2Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": longText2BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice2Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition2Id,
                "value": "true",
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice2BlockId,
                "parentLogicItemId": logicItem2Id
              }
            ]
          }
        ],
        "horizontalAlignment": false,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longText2Id,
        "blockId": longText2BlockId,
        "content": "Great! What would the product help you with?",
        "manditory": false,
        "order": 7,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Tell us the problems it would solve for you.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem3Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": longText4BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": longText2Id,
            "conditions": []
          }
        ],
        "horizontalAlignment": false,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longText3Id,
        "blockId": longText3BlockId,
        "content": "Got it. What made you decide it's not the right fit?",
        "manditory": false,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your insight helps us improve.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem4Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": longText4BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": longText3Id,
            "conditions": []
          }
        ],
        "horizontalAlignment": false,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longText4Id,
        "blockId": longText4BlockId,
        "content": "Any other thoughts or suggestions?",
        "manditory": false,
        "order": 9,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We're all ears.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": `Thanks for sharing your feedback,  {{${contactInfoBlockId}__first_name}}!`,
        "manditory": false,
        "order": 10,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "It really helps us make the product better.",
        "thirdContent": "Back to Website",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 46,
        "media": {
          "provider": "UNSPLASH",
          "id": endScreenMediaId,
          "type": "image",
          "name": "a yellow cell phone laying on an orange surface",
          "link": "https://unsplash.com/photos/PSZ0pUx3FEE/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw5fHxwaG9uZSUyMGhhcHB5JTIwb3JhbmdlfGVufDB8fHx8MTc2NDQ2NjU0OXww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      }
    ],
    "snippets": [],
    "metaTitle": "Demo Feedback Form",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateDemoFeedbackFormTemplate;